import { useContext, useEffect, useState, useRef } from 'react'
import { useFormikContext, Field, ErrorMessage } from 'formik'
import { FormContent, FormHeader, FormError, CodeInput, ButtonDrawer } from '~/components'
import { StepperContext } from '~/components/ui/ViewStepper'
import { StoreContext } from '~/store'
import { validatePhoneOtp, resendPhoneOtp, completeActivation, addEmail, isBadState } from '~/lib/api'
import { emitCustomEvent } from 'react-custom-events'
import { useTranslation } from 'react-i18next'

export default function SocialValidatePhone({ fullscreen }) {
  const { t } = useTranslation()
  const inputRef = useRef()
  const { prev, next, parentContext } = useContext(StepperContext)
  const { loginAndFetchBookings } = useContext(StoreContext)
  const { values, setFieldValue, setFieldError, setFieldTouched } = useFormikContext()
  const [isLoading, setIsLoading] = useState(false)
  const [isResendDisabled, setIsResendDisabled] = useState(true)
  const [isResendLoading, setIsResendLoading] = useState(false)
  const [activationBadState, setActivationBadState] = useState(false)

  const handleLoginComplete = (userId) => {
    loginAndFetchBookings(userId)
    if (fullscreen) {
      parentContext?.next('confirm')
    } else {
      emitCustomEvent('SHOW_LOGIN', false)
    }
  }

  // Marketing consent is asked after login by MarketingConsentModal, so only an
  // outstanding terms consent still needs the acceptTerms screen mid-flow.
  const needsConsent = () => values.shouldConsentToTermsAndConditions

  const completeIfNeeded = (onDone) => {
    if (needsConsent()) {
      next('acceptTerms')
      return
    }
    if (onDone) {
      onDone()
      return
    }
    // Nothing left to ask for, so finish the activation here — without this the
    // flow would silently stall on this screen. Returned so the caller's promise
    // chain keeps the spinner up until it resolves.
    return completeActivation({ termsAndConditions: true })
      .then(activation => {
        if (activation.userId) handleLoginComplete(activation.userId)
      })
      .catch(error => setFieldError('phoneCode', error?.response?.message))
  }

  const makeRequest = () => {
    setIsLoading(true)
    validatePhoneOtp(values.phoneCode)
      .then(response => {
        if (response?.shouldConsentToMarketing !== undefined) {
          setFieldValue('shouldConsentToMarketing', response.shouldConsentToMarketing, false)
        }
        const state = response?.deviceActivationState
        if (state === 'PHONE_OTP_COMPLETE_NEW_USER') {
          next('inputName')
        } else if (state === 'PHONE_OTP_COMPLETE_UPDATED_USER') {
          if (response.userInfo) {
            setFieldValue('existingUserName', `${response.userInfo.firstName || ''} ${response.userInfo.lastName || ''}`.trim(), false)
            setFieldValue('existingUserEmail', response.userInfo.email || response.maskedEmail || '', false)
          } else if (response.maskedEmail) {
            setFieldValue('existingUserEmail', response.maskedEmail, false)
          }
          next('confirmExistingProfile')
        } else if (state === 'PHONE_OTP_COMPLETE_EXISTING_DEVICE') {
          if (response.expectedActions?.includes('SELECT_EMAIL')) {
            if (response.externalEmail) setFieldValue('externalEmail', response.externalEmail, false)
            setFieldValue('maskedEmail', response.maskedEmail || '', false)
            next('socialSelectEmail')
          } else if (response.expectedActions?.includes('ADD_EMAIL') && values.externalEmail) {
            addEmail(values.externalEmail)
              .then(activation => {
                if (activation.userId) {
                  handleLoginComplete(activation.userId)
                } else if (activation.deviceActivationState === 'EMAIL_OTP_INITIATED') {
                  setFieldValue('maskedEmail', activation.maskedEmail || '', false)
                  next('validateEmail')
                } else {
                  return completeIfNeeded()
                }
              })
              .catch(error => setFieldError('phoneCode', error?.response?.message))
              .finally(() => setIsLoading(false))
            return
          } else {
            return completeIfNeeded()
          }
        } else if (state === 'ACTIVATION_COMPLETE_EXISTING_USER') {
          handleLoginComplete(response.userId)
        } else if (response?.success === true) {
          if (response.userId) {
            handleLoginComplete(response.userId)
          } else {
            return completeIfNeeded()
          }
        } else if (state === 'EMAIL_OTP_INITIATED') {
          setFieldValue('maskedEmail', response.maskedEmail, false)
          next('validateEmail')
        }
      })
      .catch(error => {
        setFieldError('phoneCode', error?.response?.message)
        if (isBadState(error)) setActivationBadState(true)
      })
      .finally(() => setIsLoading(false))
  }

  const handleResend = () => {
    setIsResendLoading(true)
    resendPhoneOtp()
      .then(() => {
        setFieldValue('phoneCode', '')
        inputRef.current?.focus()
      })
      .catch(error => {
        setFieldTouched('phoneCode', true, true)
        setTimeout(() => setFieldError('phoneCode', error?.response?.message), 100)
        if (isBadState(error)) setActivationBadState(true)
      })
      .finally(() => {
        setIsResendLoading(false)
        setIsResendDisabled(true)
      })
  }

  useEffect(() => {
    let id
    if (isResendDisabled) {
      id = setTimeout(() => setIsResendDisabled(false), 5000)
    }
    return () => clearTimeout(id)
  }, [isResendDisabled])

  useEffect(() => {
    if (values.phoneCode.length === 4) makeRequest()
  }, [values.phoneCode])

  const buttons = [
    { func: prev, isBack: true },
    { func: handleResend, title: t('button-did-not-get-code'), disabled: isResendDisabled || activationBadState, isLoading: isResendLoading },
  ]

  return (
    <>
      <FormContent isLoading={isLoading || isResendLoading} fullscreen={fullscreen}>
        <FormHeader isLoading={isLoading}>{t('header-enter-your-code')}</FormHeader>
        <div className="pb-4 flex flex-col gap-4">
          <div>
            <Field name="phoneCode" inputRef={inputRef} component={CodeInput} />
            <ErrorMessage name="phoneCode" component={FormError} />
          </div>
          <p className="text-body-lg text-on-surface-variant m-0">
            {t('info-enter-code-sent-social', { dialCode: values.dialCode, phone: values.phone })}
          </p>
        </div>
      </FormContent>
      <ButtonDrawer buttons={buttons} isLoading={isLoading || isResendLoading} fullscreen={fullscreen} />
    </>
  )
}
